'use client';

import { X, Plus, Minus, Trash2, ShoppingBag, ArrowRight } from 'lucide-react';
import { useCart } from '@/lib/cart-store';

export default function CartDrawer() {
  const {
    items,
    isOpen,
    closeCart,
    updateQuantity,
    removeItem,
    openCheckout,
    getTotal,
    getCount,
  } = useCart();

  const total = getTotal();
  const count = getCount();

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 z-[90] bg-black/70 backdrop-blur-sm transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={closeCart}
      />

      {/* Drawer */}
      <aside
        className={`fixed top-0 right-0 bottom-0 z-[95] w-full sm:w-[440px] bg-[#0a0a0f] border-l border-[#D4AF37]/25 shadow-2xl transition-transform duration-400 ease-out flex flex-col ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-[#D4AF37]/20">
          <div className="flex items-center gap-3">
            <ShoppingBag size={20} className="text-[#D4AF37]" />
            <div>
              <div className="font-display text-base font-semibold text-[#F4E8C1] tracking-wide uppercase">
                Votre Panier
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]/80">
                {count} {count === 1 ? 'article' : 'articles'}
              </div>
            </div>
          </div>
          <button
            onClick={closeCart}
            className="w-9 h-9 flex items-center justify-center border border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0a0a0f] transition-all"
            aria-label="Fermer le panier"
          >
            <X size={16} />
          </button>
        </div>

        {/* Items */}
        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center p-10 text-center">
            <ShoppingBag size={48} className="text-[#D4AF37]/30 mb-5" />
            <p className="font-serif italic text-lg text-[#F4E8C1]/60 mb-2">
              Votre panier est vide
            </p>
            <p className="text-xs text-[#F4E8C1]/40 max-w-xs leading-relaxed">
              Découvrez nos collections d'exception et ajoutez vos pièces
              préférées pour commencer votre commande.
            </p>
            <button
              onClick={closeCart}
              className="mt-7 px-7 py-3 bg-gold-gradient text-[#0a0a0f] font-semibold uppercase tracking-[0.18em] text-xs hover:glow-gold transition-all"
            >
              Découvrir la boutique
            </button>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto">
            {items.map((item) => (
              <div
                key={item.id}
                className="flex gap-4 p-4 border-b border-[#D4AF37]/12 hover:bg-[#12121a]/50 transition-colors"
              >
                {/* Image */}
                <div className="flex-shrink-0 w-20 h-24 overflow-hidden border border-[#D4AF37]/20">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-full object-cover"
                    style={{ filter: 'brightness(0.92) saturate(1.05)' }}
                  />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="text-[9px] uppercase tracking-[0.2em] text-[#D4AF37]/85 mb-1">
                    {item.category}
                  </div>
                  <h4 className="font-display text-sm font-semibold text-[#F4E8C1] mb-1 leading-tight">
                    {item.name}
                  </h4>
                  <div className="font-display text-sm text-gold-gradient font-bold mb-2">
                    {item.price.toLocaleString('fr-BE')} €
                  </div>

                  {/* Quantity */}
                  <div className="flex items-center gap-3">
                    <div className="flex items-center border border-[#D4AF37]/30">
                      <button
                        onClick={() =>
                          updateQuantity(item.id, item.quantity - 1)
                        }
                        className="w-7 h-7 flex items-center justify-center text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0a0a0f] transition-colors"
                        aria-label="Réduire"
                      >
                        <Minus size={12} />
                      </button>
                      <span className="w-8 text-center text-xs text-[#F4E8C1] font-medium">
                        {item.quantity}
                      </span>
                      <button
                        onClick={() =>
                          updateQuantity(item.id, item.quantity + 1)
                        }
                        className="w-7 h-7 flex items-center justify-center text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0a0a0f] transition-colors"
                        aria-label="Augmenter"
                      >
                        <Plus size={12} />
                      </button>
                    </div>

                    <button
                      onClick={() => removeItem(item.id)}
                      className="text-[#F4E8C1]/40 hover:text-[#CE1126] transition-colors p-1"
                      aria-label="Retirer"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                {/* Subtotal */}
                <div className="text-right flex-shrink-0">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#F4E8C1]/45 mb-1">
                    Sous-total
                  </div>
                  <div className="font-display text-sm text-[#F4E8C1] font-semibold">
                    {(item.price * item.quantity).toLocaleString('fr-BE')} €
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t border-[#D4AF37]/20 p-5 bg-[#070709]">
            {/* Total */}
            <div className="flex items-baseline justify-between mb-2">
              <span className="text-[10px] uppercase tracking-[0.25em] text-[#F4E8C1]/55">
                Sous-total
              </span>
              <span className="font-display text-xl text-[#F4E8C1] font-bold">
                {total.toLocaleString('fr-BE')} €
              </span>
            </div>
            <div className="flex items-baseline justify-between mb-4 pb-4 border-b border-[#D4AF37]/15">
              <span className="text-[10px] uppercase tracking-[0.25em] text-[#F4E8C1]/55">
                Livraison
              </span>
              <span className="text-xs text-[#D4AF37] italic">
                Offerte dès 500 €
              </span>
            </div>
            <div className="flex items-baseline justify-between mb-5">
              <span className="text-xs uppercase tracking-[0.2em] text-[#F4E8C1] font-semibold">
                Total
              </span>
              <span className="font-display text-2xl text-gold-gradient font-bold">
                {total.toLocaleString('fr-BE')} €
              </span>
            </div>

            {/* CTA */}
            <button
              onClick={openCheckout}
              className="w-full inline-flex items-center justify-center gap-3 py-4 bg-gold-gradient text-[#0a0a0f] font-semibold uppercase tracking-[0.2em] text-xs hover:glow-gold-strong transition-all duration-400"
            >
              Passer la commande
              <ArrowRight size={14} />
            </button>

            <p className="text-center text-[9px] uppercase tracking-[0.2em] text-[#F4E8C1]/40 mt-3">
              Paiement sécurisé · Livraison Anvers 24-48h · Monde 5-10 j
            </p>
          </div>
        )}
      </aside>
    </>
  );
}
